/**
 * StickyContainer — absolutely positioned overlay for CTA buttons
 *
 * Floats above the scrollable content area inside FlexibleLayout's .container.
 * Anchors to top or bottom, with configurable offset, background, border, and padding.
 * Renders button sections using the same SectionWrapper + SectionRenderer composition.
 *
 * Returns null if stickyContainer is absent or has no sections.
 */

import React, { memo } from 'react';
import type { CSSProperties } from 'react';
import type { LayoutSection, StepNode } from '@segmently/schema-models/v2';
import type { StickyContainerConfig } from '../../../types/stickyContainer';
import type { OnActionCallback, OnEventCallback } from '../../../types/events';
import type { TemplateVariables } from '../../../utils/contentResolver';
import type { PreviewMode } from '../../../types/previewMode';
import type { ButtonVisibilityState } from '../../../utils/buttonVisibility';
import { mapEdgeInsetsToCSS, mapCornerRadiusToCSS, hexToRgba } from '../../../utils/styleMapper';
import { SectionWrapper } from './SectionWrapper';
import { SectionRenderer } from './SectionRenderer';
import type { SectionRendererProps } from './SectionRenderer';
import { buildLinkedProductRenderResult } from './linkedProductFallbacks';

export interface StickyContainerProps {
  containerRef?: React.Ref<HTMLDivElement>;
  stickyContainer: StickyContainerConfig;
  step: StepNode;
  locale: string;
  variables?: TemplateVariables;
  onAction?: OnActionCallback;
  onEvent?: OnEventCallback;
  previewMode?: PreviewMode;
  editMode?: boolean;
  /** Per-section button visibility state (sectionId → { disabled, hidden }) */
  sectionButtonStates?: Record<string, ButtonVisibilityState>;
  /** Transient purchase state; ordinary navigation buttons remain available. */
  isLoading?: boolean;
  /** Full FlexibleLayout section pool used to resolve parentSectionId links */
  allSections?: LayoutSection[];
  /** Per-section selected product index for ProductCatalog sections (sectionId → index) */
  selectedProductIndexMap?: Map<string, number>;
  /** Per-product template variables for ProductCatalog sections */
  itemVariablesMap?: Record<string, TemplateVariables>;
  /** Edit path prefix for event element routing (e.g. 'content.flexibleLayout') */
  editPath?: string;
}

export const StickyContainer = memo(function StickyContainer({
  stickyContainer,
  containerRef,
  step,
  locale,
  variables,
  onAction,
  onEvent,
  previewMode,
  sectionButtonStates,
  isLoading = false,
  allSections,
  selectedProductIndexMap,
  itemVariablesMap,
  editPath,
}: StickyContainerProps): React.ReactElement | null {
  const { sections, anchor, offset, backgroundColor, cornerRadius, border, padding, showBackground, opacity } = stickyContainer;

  if (!sections || sections.length === 0) return null;

  // Position: absolute overlay anchored to top or bottom
  const style: CSSProperties = {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 200,
    // Horizontal insets
    marginLeft: offset?.start,
    marginRight: offset?.end,
  };

  // Anchor direction
  if (anchor === 'top') {
    style.top = offset?.top ?? 0;
  } else {
    style.bottom = offset?.bottom ?? 0;
  }

  // Background, corner radius, and border — only when showBackground is not false
  if (showBackground !== false) {
    if (backgroundColor) {
      const alpha = opacity ?? 1.0;
      style.backgroundColor = alpha < 1.0 ? hexToRgba(backgroundColor, alpha) : backgroundColor;
    }

    Object.assign(style, mapCornerRadiusToCSS(cornerRadius));

    if (border?.width !== undefined) {
      style.borderStyle = 'solid';
      style.borderWidth = border.width;
      style.borderColor = border.color ?? 'transparent';
    }
  }

  // Padding
  Object.assign(style, mapEdgeInsetsToCSS(padding));

  const sortedSections = [...sections].sort((a, b) => a.order - b.order);

  return (
    <div ref={containerRef} style={style} data-testid="sticky-container">
      {sortedSections.map((section) => {
        const buttonState = sectionButtonStates?.[section.id];
        if (buttonState?.hidden) return null;

        const sectionEditPath = editPath ? `${editPath}.stickyContainer.sections[${section.id}]` : undefined;
        const linkedProductRender = buildLinkedProductRenderResult({
          section,
          sections: allSections ?? sections,
          selectedProductIndexMap,
          itemVariablesMap,
          variables,
          locale,
        });

        const sectionRendererProps: SectionRendererProps = {
          section: linkedProductRender.section,
          purchaseProductId: linkedProductRender.purchaseProductId,
          step,
          locale,
          variables: linkedProductRender.variables,
          onAction,
          onEvent,
          previewMode,
          buttonDisabled: buttonState?.disabled,
          isLoading,
          editPath: sectionEditPath,
        };

        return (
          <SectionWrapper key={section.id} layout={section.layout} locale={locale} data-section-id={section.id}>
            <SectionRenderer {...sectionRendererProps} />
          </SectionWrapper>
        );
      })}
    </div>
  );
});

StickyContainer.displayName = 'StickyContainer';
